import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, BookOpen, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { getClasses } from "../../services/classes";
import { getCourses } from "../../services/courses";
import { getTeacherById } from "../../services/teachers";

interface ClassAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
}

interface ClassSubjectAssignment {
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  schedule?: string;
}

const ClassAssignmentModal: React.FC<ClassAssignmentModalProps> = ({
  isOpen,
  onClose,
  teacherId,
}) => {
  const queryClient = useQueryClient();
  const [assignments, setAssignments] = useState<ClassSubjectAssignment[]>([]);
  const [newAssignment, setNewAssignment] = useState({
    classId: "",
    subjectId: "",
    schedule: "",
  });

  // Fetch teacher data
  const { data: teacher } = useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: () => getTeacherById(teacherId),
    enabled: isOpen && !!teacherId,
  });

  // Fetch available classes
  const {
    data: classesResponse,
    error: classesError,
    isLoading: classesLoading,
  } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getClasses({ limit: 100 }),
    enabled: isOpen,
  });

  // Fetch available courses/subjects
  const {
    data: coursesResponse,
    error: coursesError,
    isLoading: coursesLoading,
  } = useQuery({
    queryKey: ["courses"],
    queryFn: () => getCourses({ limit: 100, isActive: true }),
    enabled: isOpen,
  });

  const classes = classesResponse?.data || [];
  const courses = coursesResponse?.data || [];

  // Assign single class-subject mutation
  const assignClassMutation = useMutation({
    mutationFn: async (data: {
      classId: string;
      courseId: string;
      isClassTeacher?: boolean;
    }) => {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_URL}/api/teachers/${teacherId}/assign-class`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            classId: data.classId,
            courses: [data.courseId], // Backend expects array of course IDs
            isClassTeacher: data.isClassTeacher || false,
          }),
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to assign class");
      }
      return response.json();
    },
    onSuccess: (response) => {
      toast.success("Class and subject assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      setNewAssignment({ classId: "", subjectId: "", schedule: "" });

      // Refresh assignments by reloading teacher data
      // The useEffect will handle updating the assignments state
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to assign class and subject");
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: async (data: { classId: string; courseId: string }) => {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_URL}/api/teachers/${teacherId}/remove-class/${data.classId}?courseId=${data.courseId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove assignment");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success("Course removed from class assignment successfully");
      queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      // The assignments will be refreshed via the useEffect when teacher data updates
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove assignment");
    },
  });

  const addAssignment = () => {
    if (!newAssignment.classId || !newAssignment.subjectId) {
      toast.error("Please select both a class and a subject");
      return;
    }

    // Check if this combination already exists in current assignments
    const isDuplicate = assignments.some(
      (a) =>
        a.classId === newAssignment.classId &&
        a.subjectId === newAssignment.subjectId,
    );

    if (isDuplicate) {
      toast.error("This class-subject combination already exists");
      return;
    }

    // Directly call the API
    assignClassMutation.mutate({
      classId: newAssignment.classId,
      courseId: newAssignment.subjectId,
      isClassTeacher: false, // Default to false, could be made configurable
    });
  };

  const removeAssignment = (index: number) => {
    const assignment = assignments[index];
    if (!assignment) return;

    // Optimistically remove from local state
    setAssignments((prev) => prev.filter((_, i) => i !== index));

    removeAssignmentMutation.mutate({
      classId: assignment.classId,
      courseId: assignment.subjectId,
    });
  };

  const handleSave = () => {
    // Since we're now saving each assignment immediately,
    // this function can just close the modal
    onClose();
  };

  React.useEffect(() => {
    if (isOpen && teacher?.data) {
      const teacherData = teacher.data as any;
      // Load existing class and subject assignments from teacher data
      const existingAssignments: ClassSubjectAssignment[] = [];

      // Process teacher's classes with their courses
      if (teacherData.classes && Array.isArray(teacherData.classes)) {
        teacherData.classes.forEach((classAssignment: any) => {
          if (classAssignment.classId && classAssignment.courses) {
            classAssignment.courses.forEach((course: any) => {
              existingAssignments.push({
                classId: classAssignment.classId._id || classAssignment.classId,
                className: classAssignment.classId.name || "Unknown Class",
                subjectId: course._id || course,
                subjectName: course.name || "Unknown Subject",
                schedule: "", // Schedule not stored in current structure
              });
            });
          }
        });
      }

      setAssignments(existingAssignments);
    }
  }, [isOpen, teacher]);

  if (!isOpen) return null;

  const teacherData = teacher?.data as any;

  // Get teacher name from either userInfo or userId fields
  let firstName = "";
  let lastName = "";

  if (teacherData?.userInfo) {
    firstName = teacherData.userInfo.firstName || "";
    lastName = teacherData.userInfo.lastName || "";
  } else if (typeof teacherData?.userId === "object") {
    firstName = teacherData.userId.firstName || "";
    lastName = teacherData.userId.lastName || "";
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-blue-600" />
            Assign Classes & Subjects to {firstName} {lastName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Add New Assignment */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-4">
              Add New Class & Subject Assignment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Class
                </label>
                <select
                  value={newAssignment.classId}
                  onChange={(e) =>
                    setNewAssignment((prev) => ({
                      ...prev,
                      classId: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Class</option>
                  {classes.map((classItem) => (
                    <option key={classItem._id} value={classItem._id}>
                      {classItem.name} - {classItem.grade}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject/Course
                </label>
                <select
                  value={newAssignment.subjectId}
                  onChange={(e) =>
                    setNewAssignment((prev) => ({
                      ...prev,
                      subjectId: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a subject/course</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.name}({course.level})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule (Optional)
                </label>
                <input
                  type="text"
                  value={newAssignment.schedule}
                  onChange={(e) =>
                    setNewAssignment((prev) => ({
                      ...prev,
                      schedule: e.target.value,
                    }))
                  }
                  placeholder="e.g., Mon-Fri 9:00-10:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={addAssignment}
              disabled={assignClassMutation.isPending}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {assignClassMutation.isPending
                ? "Assigning..."
                : "Add Assignment"}
            </button>
          </div>

          {/* Current Assignments */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              Current Class & Subject Assignments
            </h3>
            {assignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <p>No classes assigned yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center"
                  >
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {assignment.className}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Subject: {assignment.subjectName}
                      </p>
                      {assignment.schedule && (
                        <p className="text-sm text-gray-500">
                          Schedule: {assignment.schedule}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeAssignment(index)}
                      disabled={removeAssignmentMutation.isPending}
                      className={`p-1 ${
                        removeAssignmentMutation.isPending
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-red-600 hover:text-red-900"
                      }`}
                      title="Remove Assignment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassAssignmentModal;
